import { getChartColor } from "@/composables/chartPalette";
import {
  TECHNOLOGIES,
  technologyKeyForCode,
  technologyNameForCode,
} from "@/types/technology";

export interface DeviceMapMarker {
  lat: number;
  lng: number;
  device_id: number;
  device_name: string | null;
  device_eic: string;
  device_power: number | null;
  technology_code: string | null;
  address: string | null;
  company_id: number;
  company_name: string | null;
  country: string | null;
}

export const UNKNOWN_TECH_KEY = "unknown";
export const UNKNOWN_TECH_COLOR = "#ADB3AF";

/** Resolve a raw registry technology code (e.g. "T030000") to a registry key, or `"unknown"`. */
export const technologyKeyFromCode = (code: string | null): string =>
  (code ? technologyKeyForCode(code) : null) ?? UNKNOWN_TECH_KEY;

/** Human name for a raw technology code ("T030000" → "Hydro"); an unmatched code is shown as it came. */
export const technologyNameFromCode = (code: string | null): string =>
  code === null ? "Unknown" : technologyNameForCode(code);

/** Brand palette color for a raw technology code. */
export const technologyColorFromCode = (code: string | null): string => {
  const key = technologyKeyFromCode(code);
  if (key === UNKNOWN_TECH_KEY) return UNKNOWN_TECH_COLOR;
  const name = TECHNOLOGIES[key as keyof typeof TECHNOLOGIES].name;
  return getChartColor({ field: "technology", value: name });
};

/** Device power is stored in kW; render MW above 1 MW. */
export const formatPower = (powerKw: number | null): string => {
  if (powerKw == null) return "N/A";
  if (powerKw >= 1000) {
    const mw = powerKw / 1000;
    return `${mw >= 100 ? Math.round(mw) : mw.toFixed(1)} MW`;
  }
  return `${Math.round(powerKw)} kW`;
};

// Marker radius: area proportional to capacity (sqrt scale) so a 100 MW plant
// doesn't dwarf the map and rooftop solar stays visible.
const MIN_RADIUS = 5;
const MAX_RADIUS = 22;
const REFERENCE_POWER_KW = 100_000; // ~100 MW fills the scale

export const markerRadius = (powerKw: number | null): number => {
  if (!powerKw || powerKw <= 0) return MIN_RADIUS;
  const scale = Math.sqrt(
    Math.min(powerKw, REFERENCE_POWER_KW) / REFERENCE_POWER_KW
  );
  return MIN_RADIUS + scale * (MAX_RADIUS - MIN_RADIUS);
};

const escapeHtml = (value: string | number | null | undefined): string =>
  value == null
    ? "N/A"
    : String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;");

/** Popup markup for a device marker (Leaflet popups take raw HTML). */
export const devicePopupHtml = (device: DeviceMapMarker): string => {
  const techName = technologyNameFromCode(device.technology_code);
  const techColor = technologyColorFromCode(device.technology_code);
  return `
    <div class="device-map-popup">
      <div class="device-map-popup__title">${escapeHtml(device.device_name ?? device.device_eic)}</div>
      <div class="device-map-popup__tech">
        <span class="device-map-popup__dot" style="background:${techColor}"></span>
        ${escapeHtml(techName)} · ${escapeHtml(formatPower(device.device_power))}
      </div>
      <div class="device-map-popup__row">${escapeHtml(device.company_name)} <span class="text-muted">(id: ${escapeHtml(device.company_id)})</span></div>
      <div class="device-map-popup__row text-muted">${escapeHtml(device.address)}</div>
      <div class="device-map-popup__row text-muted">EIC: ${escapeHtml(device.device_eic)}</div>
    </div>
  `;
};
